/**
 * AI Tools — client helpers for summarize + ask (RAG with tool loop).
 * Uses the shared axios instance (auth header injected automatically).
 */
import axios from '@services/axios';
import { useSessionStore } from '@stores/session';

function apiBase(): string {
  const domain = useSessionStore().appConfig?.domain;
  if (!domain) throw new Error('App configuration is not loaded yet');
  return domain;
}

export type SummarizeStyle = 'brief' | 'detailed' | 'bullets' | 'meeting-notes' | 'chapters';

export interface SummarizeRequest {
  elementId?: string;
  projectId?: string;
  outputId?: string;
  secretId?: string;
  fileName?: string;
  text?: string;
  style?: SummarizeStyle;
  language?: string;
  maxTokens?: number;
}

export interface SummarizeResponse {
  summary: string;
  fileName: string;
  style: string;
  truncated: boolean;
  model: string;
  usage?: any;
}

export async function summarize(req: SummarizeRequest): Promise<SummarizeResponse> {
  const { data } = await axios.post(`${apiBase()}api/ai/summarize`, {
    ...req,
    action: 'summarize',
  });
  return data as SummarizeResponse;
}

export interface AskRequest {
  question: string;
  scope?: 'single' | 'global';
  elementId?: string;
  projectId?: string;
  outputId?: string;
  secretId?: string;
  conversationHistory?: Array<{ role: 'user' | 'assistant'; content: string }>;
  /** Page-aware agent routing */
  agent?: 'transcript' | 'global' | 'page-help';
  pageContext?: { path: string; name: string; pageType: string; label: string };
}

export interface Citation {
  elementId: string;
  fileName: string;
  start?: number;
  end?: number;
  snippet: string;
}

export interface AskResponse {
  answer: string;
  citations: Citation[];
  scope: string;
  elementId?: string;
  model: string;
  noResults?: boolean;
}

export async function ask(req: AskRequest): Promise<AskResponse> {
  const { data } = await axios.post(`${apiBase()}api/ai/ask`, {
    ...req,
    action: 'ask',
  });
  return data as AskResponse;
}

/** Convenience: ask via the unified /tools route */
export async function askViaTools(req: AskRequest): Promise<AskResponse> {
  const { data } = await axios.post(`${apiBase()}api/ai/tools`, {
    ...req,
    action: 'ask',
  });
  return data as AskResponse;
}

export type DocType = 'receipt' | 'invoice' | 'id' | 'contract' | 'other';
export interface ExtractRequest { docType?: DocType; elementId?: string; projectId?: string; outputId?: string; secretId?: string; text?: string; }
export async function extractReceipt(req: ExtractRequest): Promise<any> {
  const { data } = await axios.post(`${apiBase()}api/ai/extract`, { ...req, action: 'extract', docType: req.docType || 'receipt' });
  return data;
}
export async function extractDocument(req: ExtractRequest): Promise<any> {
  return extractReceipt(req);
}

export function formatTime(s?: number): string {
  if (s == null || Number.isNaN(s)) return '';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, '0')}`;
}
